import React from 'react';
import PropTypes from 'prop-types';

import RichText from '$partials/global/rich-text';
import Button from '$partials/ui/button';
import { Image } from '$partials/media';
import { useLocale } from '$contexts/locale.jsx';
import { IconChevronSprite } from '$partials/icons/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

const propTypes = {
    title: PropTypes.string,
    card: PropTypes.shape({
        cardBackgroundColour: PropTypes.string,
        cardTitle: PropTypes.string,
        ctaLabel: PropTypes.string,
        ctaLink: PropTypes.string,
        eyebrowIcon: PropTypes.shape({
            height: PropTypes.number.isRequired,
            src: PropTypes.string.isRequired,
            width: PropTypes.number.isRequired,
        }),
        image: PropTypes.shape({
            src: PropTypes.string.isRequired,
        }),
        richText: PropTypes.object,
    }),
};

/**
 * @type {React.FC<PropTypes.InferProps<typeof propTypes>>}
 */
const LargeCardCtaVariant = ({ title, card }) => {
    const { formatURI } = useLocale();

    const {
        cardBackgroundColour,
        cardTitle,
        ctaLabel,
        ctaLink,
        eyebrowIcon,
        image,
        richText,
    } = card ?? {};

    const isGreen = cardBackgroundColour === 'Green';
    const iconAspectRatio = eyebrowIcon?.width && eyebrowIcon?.height
        ? eyebrowIcon.width / eyebrowIcon.height
        : 1;

    return (
        <div className='bg-white relative py-40 md:py-50'>
            <div className='layout-container'>
                {title && (
                    <div className={'w-full mb-40 px-10 sm:px-20 flex flex-wrap'}>
                        <p className='type-style-3'>{title}</p>
                    </div>
                )}
                <div className='flex'>

                    {/* spacer */}
                    <div className='h-full relative px-10 sm:px-5 md:px-10 hidden md:block md:w-4/12'/>

                    <section className='h-full relative px-10 sm:px-5 md:px-10 w-full md:w-8/12'>
                        <div
                            className={`${isGreen ? 'bg-expanded-moss' : 'bg-sand'}
                            px-15 pt-15 pb-25 sm:p-0 rounded-button overflow-hidden h-full flex flex-col gap-25 sm:gap-0 sm:flex-row justify-between mt-0`}>
                            {/* Text */}
                            <div className={`flex flex-col sm:px-25 sm:pt-25 md:p-40 ${!image ? 'sm:w-10/12 sm:pb-25' : 'sm:pb-60'}`}>
                                {eyebrowIcon && (
                                    <div className='flex items-center mb-16'>
                                        <div
                                            className='mr-10'
                                            style={{
                                                fontSize: '18px',
                                                width: `${iconAspectRatio}em`,
                                                maxWidth: '100%'
                                            }}>
                                            <Image
                                                {...eyebrowIcon}
                                                loading={''} />
                                        </div>
                                    </div>
                                )}

                                <p className={`${isGreen ? 'text-white' : 'text-black'} type-style-3`}>
                                    {cardTitle}
                                </p>

                                {richText && (
                                    <RichText
                                        {...richText}
                                        className={`${isGreen ? 'text-white' : 'text-light-grey'} mt-[7px] sm:mt-10 md:mt-12 type-style-5 rich-text`} />
                                )}

                                {ctaLabel && ctaLink && (
                                    <Button
                                        as='a'
                                        className='button button--primary w-fit mt-20'
                                        href={formatURI(ctaLink)}
                                        type='button'>
                                        <span>{ctaLabel}</span>
                                        <span className='button__icon button__icon--animate'>
                                            <IconChevronSprite />
                                        </span>
                                    </Button>
                                )}
                            </div>

                            {/* Image */}
                            {image && (
                                <div
                                    className='relative w-full sm:w-[277px] shrink-0 rounded-button sm:rounded-none overflow-hidden sm:overflow-visible'>
                                    <Image
                                        {...image}
                                        className='object-cover h-full w-full aspect-square sm:aspect-auto'
                                        containerClassName='h-full'
                                        height={'100%'}
                                        width={'100%'} />
                                </div>
                            )}
                        </div>
                    </section>
                </div>

            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

LargeCardCtaVariant.propTypes = propTypes;

export default LargeCardCtaVariant;
